import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Switch,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import InfinityCard from "../components/InfinityCard";
import { db } from "../@shared/firebase/config";
import { MainContext } from "../App";
import NeoButton from "../components/NeoButton";
import { useNavigate } from "react-router-dom";
import bg from "../@shared/assets/deckbuilderbg.png";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { useCardStore } from "../@shared/store/useCardsStore";
import MyDecksScreen from "./MyDecksScreen";
import DeckBuilder from "./DeckBuilder";
import ViewCardModal from "../components/ViewCardModal";
import {
  canCraftCard,
  isUsedToCraftFinite,
} from "../@shared/game/cardPackEngine";
import CharacterCard from "../components/CharacterCard";
import { getColorShades } from "../@shared/utils/Utils";
import {
  ArrowBackIos,
  Filter,
  FilterListOffRounded,
  FilterListRounded,
  LoopRounded,
  SortRounded,
  SwapVertRounded,
} from "@mui/icons-material";

const MyCardsScreen = () => {
  const { Theme, deviceType, setScreenLoading } = React.useContext(MainContext);
  const { userData } = useAuthStore();
  const { infinityCards, characterCards } = useCardStore();
  const { decks, cards, nftCards } = userData;

  const [sortOption, setSortOption] = useState("name");
  const [filterOptions, setFilterOptions] = useState({
    rarity: [],
    element: [],
    collection: [],
    affectedArea: [],
    finite: null,
  });

  const isMobile = deviceType === "mobile";

  const [showNFTCards, setShowNFTCards] = useState(false);
  const navigate = useNavigate();

  const [screen, setScreen] = useState("Cards"); // Default screen is 'Cards' / 'Decks' / 'Build'
  const [newDeck, setNewDeck] = useState([]);
  const [selectedDeckID, setSelectedDeckID] = useState(null);
  const [deckName, setDeckName] = useState("");
  const [seletedCharacter, setSelectedCharacter] = useState(null);
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [totalAmountOfCards, setTotalAmountOfCards] = useState(0);
  const [totalInfinityCardsOwned, setTotalInfinityCardsOwned] = useState(null);
  const [totalCharacterCardsOwned, setTotalCharacterCardsOwned] =
    useState(null);

  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [nftFilterAnchorEl, setNftFilterAnchorEl] = useState(null);
  const [charFilterAnchorEl, setCharFilterAnchorEl] = useState(null);

  const openSortMenu = Boolean(sortAnchorEl);
  const openNftFilter = Boolean(nftFilterAnchorEl);
  const openCharFilter = Boolean(charFilterAnchorEl);

  const handleSortClick = (e) => setSortAnchorEl(e.currentTarget);
  const handleNftFilterClick = (e) => setNftFilterAnchorEl(e.currentTarget);
  const handleCharFilterClick = (e) => setCharFilterAnchorEl(e.currentTarget);

  const handleSortClose = () => setSortAnchorEl(null);
  const handleNftFilterClose = () => setNftFilterAnchorEl(null);
  const handleCharFilterClose = () => setCharFilterAnchorEl(null);

  const getColors = (color) => {
    if (
      color === Theme.palette.primary.main ||
      color === Theme.palette.secondary.main
    ) {
      return {
        light: `${color}Active`,
        dark: `${color}Hover`,
        shadow: `${color}`,
      };
    }

    return getColorShades(color);
  };

  const calculateTotalCards = (cards) => {
    let total = cards.reduce((acc, card) => acc + card.amountOwned, 0);
    console.log("total", total);
    return total;
  };

  //Cards Screen State
  const [selectedCard, setSelectedCard] = useState(null);
  const craftableCards = infinityCards.filter((c) => canCraftCard(c, cards));

  console.log("craftableCards", craftableCards);

  // Ref for the scrollable cards container.
  const cardsContainerRef = useRef(null);

  const reset = () => {
    setNewDeck([]);
    setSelectedCharacter(null);
    setScreen("Decks");
    setSelectedDeckID(null);
  };

  useEffect(() => {
    console.log("Sort option changed:", sortOption);
  }, [sortOption]);

  useEffect(() => {
    setScreenLoading(false);
  }, []);

  useEffect(() => {
    if (nftCards?.length && !totalCharacterCardsOwned) {
      let total = calculateTotalCards(nftCards);
      setTotalCharacterCardsOwned(total);
    }

    if (cards?.length && !totalInfinityCardsOwned) {
      let total = calculateTotalCards(cards);
      setTotalInfinityCardsOwned(total);
    }
    console.log();
  }, [nftCards, cards, totalCharacterCardsOwned, totalInfinityCardsOwned]);

  const sortCards = (cardsToSort) => {
    if (!sortOption) return cardsToSort;

    return [...cardsToSort].sort((a, b) => {
      switch (sortOption) {
        case "name":
          return (a?.name || "").localeCompare(b?.name || "");
        case "rarity":
          return (a?.rarity || "").localeCompare(b?.rarity || "");
        case "element":
          return (a?.element || "").localeCompare(b?.element || "");
        case "collection":
          return (a?.collection || "").localeCompare(b?.collection || "");
        case "affectedArea":
          return (a?.affectedArea?.[0] || "").localeCompare(
            b?.affectedArea?.[0] || ""
          );
        case "max":
          return Number(a?.max || 0) - Number(b?.max || 0);
        case "amountOwned":
          return Number(b?.amountOwned || 0) - Number(a?.amountOwned || 0);
        case "finite":
          return (
            (a?.finite === "TRUE" ? -1 : 1) - (b?.finite === "TRUE" ? -1 : 1)
          );
        default:
          return 0;
      }
    });
  };

  const filterCards = (cardsToFilter) => {
    return cardsToFilter.filter((card) => {
      if (showNFTCards) {
        // NFT Cards
        const { rarity, element, collection } = filterOptions;
        return (
          (rarity.length === 0 || rarity.includes(card.rarity)) &&
          (element.length === 0 || element.includes(card.element)) &&
          (collection.length === 0 || collection.includes(card.collection))
        );
      } else {
        // Infinity Cards
        const { affectedArea, finite } = filterOptions;
        return (
          (affectedArea.length === 0 ||
            affectedArea.includes(card.affectedArea?.[0])) &&
          (finite === null || card.finite === finite)
        );
      }
    });
  };

  const handleClose = () => {
    setSortAnchorEl(null);
    setNftFilterAnchorEl(null);
    setCharFilterAnchorEl(null);
  };

  const handleSelectOption = (option) => {
    setSortOption(option);
    handleClose();
  };

  const handleFilterClick = (option) => {
    setFilterOptions((prev) => ({
      ...prev,
      rarity: option ? [option] : [],
    }));
    handleClose();
  };

  const rawCards = showNFTCards
    ? ownedOnly
      ? nftCards
      : characterCards
    : ownedOnly
    ? cards
    : infinityCards;

  const availableCards = sortCards(filterCards(rawCards));

  if (!userData) return <div>Loading...</div>;

  return (
    <>
      {screen === "Cards" && (
        <Grid
          container
          item
          xs={12}
          sx={{
            justifyContent: "center",
            height: "100%",
            backgroundImage: `url(${bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 1,
              backgroundColor: Theme.palette.primary.main,
              borderBottom: `5px solid ${Theme.palette.primary.dark}`,
              borderTop: `5px solid ${Theme.palette.primary.light}`,
              position: "relative",
              width: "100%",
              pt: 2,
              pb: 2,
              height: "50px",
            }}
          >
            <Box
              style={{
                position: "absolute",
                top: "50%",
                left: "2%",
                transform: "translate(0%, -50%)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <NeoButton
                isIcon={true}
                icon={
                  <ArrowBackIos
                    style={{
                      color: Theme.palette.text.main,
                      fontSize: "1.5rem",
                      paddingLeft: "10px",
                    }}
                  />
                }
                borderRadius={10}
                onClick={() => {
                  setScreenLoading(true);
                  const timeout = setTimeout(() => {
                    navigate(-1);
                  }, 500); // small delay, but lets TransitionOverlay take over

                  return () => clearTimeout(timeout);
                }}
                color={Theme.palette.tirciary.main}
              />
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "2rem", md: "3rem" },
                position: "relative",
                color: Theme.palette.text.main,
                textShadow: ` -2px -2px 0 ${Theme.palette.tirciary.dark}, 2px -2px 0 ${Theme.palette.tirciary.dark}, -2px  2px 0 ${Theme.palette.tirciary.dark}, 2px  2px 0 ${Theme.palette.tirciary.dark}`,
              }}
            >
              Cards
            </Typography>

            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: "2%",
                transform: "translate(0%, -50%)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: { xs: "100px", md: "150px" },
              }}
            >
              <NeoButton
                onClick={() => setScreen("Decks")}
                width={100}
                text="Decks"
                color={Theme.palette.tirciary.main}
              />
            </Box>
          </Box>

          {/* Scrollable Cards Container */}
          <Box
            ref={cardsContainerRef}
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "center",
              background: "#ffffff50",
              borderRadius: 2,
              height: { xs: "72%", md: "80%" },
              overflowY: "auto",
              scrollbarWidth: "none",
              width: { xs: "90%", md: "80%" },
              position: "absolute",
              top: { xs: "53%", md: "55%" },
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Grid container style={{ paddingBotom: 100 }}>
              {!showNFTCards
                ? availableCards.map((c) => {
                    const card = cards.find((c2) => c2.id === c.id) || c;
                    return (
                      <Grid
                        container
                        item
                        xs={4}
                        md={2}
                        key={card.id}
                        sx={{
                          justifyContent: "center",
                          alignItems: "center",
                          paddingBottom: "20px",
                        }}
                      >
                        <Box
                          sx={{
                            width: "fit-content",
                            position: "relative",
                            height: "fit-content",
                          }}
                        >
                          {card?.new && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "3%",
                                right: "-10%",
                                width: "50%",
                                zIndex: 100,
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  containerType: "inline-size",
                                }}
                              >
                                <img
                                  src={require("../@shared/assets/red-banner.png")}
                                  style={{
                                    width: "100%",
                                  }}
                                />
                                <Typography
                                  style={{
                                    position: "absolute",
                                    top: "55%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    color: Theme.palette.text.main,
                                    fontSize: "20cqw",
                                    fontWeight: "bold",
                                    textShadow: ` -1px -1px 0 ${Theme.palette.tirciary.dark}, 1px -1px 0 ${Theme.palette.tirciary.dark}, -1px  1px 0 ${Theme.palette.tirciary.dark}, 1px  1px 0 ${Theme.palette.tirciary.dark}`,
                                  }}
                                >
                                  NEW!
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {card.amountOwned === 0 || !card.amountOwned ? (
                            <Box
                              sx={{
                                position: "absolute",
                                // position: "absolute",
                                top: "52%",
                                left: "53%",
                                transform: "translate(-50%, -50%)",
                                width: "106%",
                                height: "105%",
                                // aspectRatio: "2.5/3.5",
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                zIndex: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "6%",
                              }}
                            >
                              <img
                                src={require("../@shared/assets/icons/lock_deck_icon2.png")}
                                alt=""
                                style={{ width: "35%" }}
                              />
                            </Box>
                          ) : null}
                          <InfinityCard
                            {...card}
                            borderPx={12}
                            onClick={() => {
                              if (card.new) {
                                const cardRef = db
                                  .collection("users")
                                  .doc(userData?.id)
                                  .collection("Infinity cards")
                                  .doc(card.id);
                                cardRef.update({
                                  new: false,
                                });
                              }
                              setSelectedCard(card);
                            }}
                            showAmountOwned={true}
                            containerHeight={
                              window.innerWidth >= 900
                                ? window.innerHeight * 0.5
                                : window.innerHeight * 0.28
                            }
                            heightPer={window.innerWidth >= 900 ? 50 : 60}
                          />
                        </Box>
                      </Grid>
                    );
                  })
                : availableCards.map((c, index) => {
                    const card = nftCards.find((c2) => c2.id === c.id) || c;
                    const { bgColor1, bgColor2 } = card;
                    const getElementImg = () => {
                      switch (card.element) {
                        case "fire":
                          return require("../@shared/assets/icons/fire.png");
                        case "water":
                          return require("../@shared/assets/icons/water.png");
                        case "grass":
                          return require("../@shared/assets/icons/grass.png");
                        case "rock":
                          return require("../@shared/assets/icons/rock.png");
                        case "air":
                          return require("../@shared/assets/icons/wind.png");
                        case "electric":
                          return require("../@shared/assets/icons/electric.png");
                        case "void":
                          return require("../@shared/assets/icons/void.png");
                        case "ice":
                          return require("../@shared/assets/icons/ice.png");
                        default:
                          return "";
                      }
                    };

                    const getElementBGColor = () => {
                      switch (card.element) {
                        case "fire":
                          return "rgb(255, 205, 130)";
                        case "water":
                          return "rgb(183, 216, 255)";
                        case "grass":
                          return "rgb(185, 255, 188)";
                        case "rock":
                          return "rgb(255, 234, 169)";
                        case "air":
                          return "rgb(209, 227, 235)";
                        case "electric":
                          return "rgb(255, 252, 173)";
                        case "void":
                          return "rgb(188, 154, 201)";
                        case "ice":
                          return "rgb(227, 246, 255)";
                        default:
                          return "";
                      }
                    };

                    const attributes = [
                      {
                        name: "Health",
                        value: card.health,
                        img: require("../@shared/assets/icons/heart.png"),
                      },
                      {
                        name: "Defense",
                        value: card.defense,
                        img: require("../@shared/assets/icons/sheild.png"),
                      },
                      {
                        name: "Attack",
                        value: card.attack,
                        img: require("../@shared/assets/icons/swords_icon2.png"),
                      },
                      {
                        name: "Accuracy",
                        value: card.accuracy,
                        img: require("../@shared/assets/icons/cross-hair.png"),
                      },
                    ];
                    return (
                      <Grid
                        item
                        xs={4}
                        md={2}
                        key={card.id}
                        sx={{
                          height: "fit-content",
                          marginBottom: "10px",
                          padding: { xs: 0, md: "10px" },
                          containerType: "inline-size",
                        }}
                      >
                        {/* WEB VIEW */}
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "2.5/3.5",
                            display: {
                              xs: "none",
                              md: "block",
                            },
                            position: "relative",
                          }}
                        >
                          {card?.new && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "3%",
                                right: "-8%",
                                width: "50%",
                                zIndex: 100,
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  containerType: "inline-size",
                                }}
                              >
                                <img
                                  src={require("../@shared/assets/red-banner.png")}
                                  style={{
                                    width: "100%",
                                  }}
                                />
                                <Typography
                                  style={{
                                    position: "absolute",
                                    top: "58%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    color: Theme.palette.text.main,
                                    fontSize: "20cqw",
                                    fontWeight: "bold",
                                    textShadow: ` -1px -1px 0 ${Theme.palette.tirciary.dark}, 1px -1px 0 ${Theme.palette.tirciary.dark}, -1px  1px 0 ${Theme.palette.tirciary.dark}, 1px  1px 0 ${Theme.palette.tirciary.dark}`,
                                  }}
                                >
                                  NEW!
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {card.amountOwned === 0 || !card.amountOwned ? (
                            <Box
                              sx={{
                                position: "absolute",
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: "100%",
                                aspectRatio: "2.5/3.5",
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                zIndex: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "6%",
                              }}
                            >
                              <img
                                src={require("../@shared/assets/icons/lock_deck_icon2.png")}
                                style={{ width: "35%" }}
                              />
                            </Box>
                          ) : null}
                          <CharacterCard
                            {...card}
                            showAmtOwned={true}
                            borderPx={12}
                            containerHeight={
                              window.innerWidth >= 900 ? 300 : 200
                            }
                            heightPer={100}
                            onClick={() => {
                              if (card?.new) {
                                const charRef = db
                                  .collection("users")
                                  .doc(userData?.id)
                                  .collection("Character cards")
                                  .doc(card.id);
                                charRef.update({
                                  new: false,
                                });
                              }
                              setSelectedCard(card);
                            }}
                          />
                        </Box>

                        {/* MOBILE VIEW */}
                        <Box
                          sx={{
                            width: "90%",
                            aspectRatio: "2.5/4",
                            background: "#ffffff50",
                            // display: "flex",
                            flexDirection: "column",
                            display: {
                              xs: "block",
                              md: "none",
                            },
                            position: "relative",
                            borderRadius: 2,
                            containerType: "inline-size",
                          }}
                          onClick={() => {
                            if (card?.new) {
                              const charRef = db
                                .collection("users")
                                .doc(userData?.id)
                                .collection("Character cards")
                                .doc(card.id);
                              charRef.update({
                                new: false,
                              });
                            }
                            setSelectedCard(card);
                          }}
                        >
                          {card?.new && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "3%",
                                right: "-10%",
                                width: "50%",
                                zIndex: 100,
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  containerType: "inline-size",
                                }}
                              >
                                <img
                                  src={require("../@shared/assets/red-banner.png")}
                                  style={{
                                    width: "100%",
                                  }}
                                />
                                <Typography
                                  style={{
                                    position: "absolute",
                                    top: "55%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    color: Theme.palette.text.main,
                                    fontSize: "20cqw",
                                    fontWeight: "bold",
                                    textShadow: ` -1px -1px 0 ${Theme.palette.tirciary.dark}, 1px -1px 0 ${Theme.palette.tirciary.dark}, -1px  1px 0 ${Theme.palette.tirciary.dark}, 1px  1px 0 ${Theme.palette.tirciary.dark}`,
                                  }}
                                >
                                  NEW!
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {card.amountOwned === 0 || !card.amountOwned ? (
                            <Box
                              sx={{
                                position: "absolute",
                                // position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: "100%",
                                height: "100%",
                                // aspectRatio: "2.5/3.5",
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                zIndex: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "6%",
                              }}
                            >
                              <img
                                src={require("../@shared/assets/icons/lock_deck_icon2.png")}
                                alt=""
                                style={{ width: "35%" }}
                              />
                            </Box>
                          ) : null}
                          <Box
                            style={{
                              width: "93%",
                              height: "75%",
                              position: "absolute",
                              border: "4px solid #000",
                              zIndex: 1,
                              borderRadius: 8,
                            }}
                          >
                            <Typography
                              style={{
                                fontWeight: 900,
                                color: "#fff",
                                fontSize: 24,
                                textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                                position: "absolute",
                                objectFit: "contain",
                                top: "-5%",
                                left: "-1%",
                                zIndex: 10,
                              }}
                            >
                              {card.rarity.split("")[0]}
                            </Typography>

                            <img
                              src={require("../@shared/assets/green_gem.webp")}
                              style={{
                                width: 36,
                                height: 36,
                                position: "absolute",
                                objectFit: "contain",
                                top: "-5%",
                                left: "-12%",
                                zIndex: 9,
                              }}
                            />
                            <Box
                              style={{
                                width: 28,
                                height: 28,
                                position: "absolute",
                                objectFit: "contain",
                                top: "-5%",
                                right: "-6%",
                                zIndex: 1,
                                backgroundColor: getElementBGColor(),
                                borderRadius: 28,
                                border: "2px solid #000",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 10,
                              }}
                            >
                              <img
                                src={getElementImg()}
                                style={{
                                  width: 20,
                                  height: 20,
                                  objectFit: "contain",
                                  zIndex: 2,
                                }}
                              />
                            </Box>

                            <Box
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: 6,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                background: bgColor1,
                                background: `linear-gradient(to bottom, ${bgColor1}, ${bgColor2})`,
                                position: "relative",
                              }}
                            >
                              {(card.rarity === "Legendary" ||
                                card.rarity === "Epic") &&
                                card.imageBG && (
                                  <img
                                    src={card.imageBG}
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, -50%)",
                                      width: "105%",
                                      height: "105%",
                                      objectFit: "cover",
                                      zIndex: 0,
                                    }}
                                  />
                                )}
                              <img
                                src={card.imageUrl}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  zIndex: 1,
                                }}
                              />
                            </Box>
                            <Box
                              style={{
                                width: "93%",
                                height: "95%",
                                borderTop: "3px solid #ffffff90",
                                borderBottom: "3px solid #ffffff30",
                                borderLeft: "3px solid #ffffff50",
                                borderRight: "3px solid #ffffff90",
                                borderRadius: 8,
                                zIndex: 2,
                                position: "absolute",
                                top: 0,
                                left: 0,
                              }}
                            />

                            <Box
                              style={{
                                position: "absolute",
                                bottom: 2,
                                borderBottomStartRadius: 6,
                                borderBottomEndRadius: 6,
                                left: 3,
                                width: "93%",
                                height: 20,
                                backgroundColor: "#00000080",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 3,
                              }}
                            >
                              <Typography
                                style={{
                                  color: "#fff",
                                  fontSize: card.name.length > 10 ? 7 : 10,
                                  fontWeight: 800,
                                  textAlign: "center",
                                  textTransform: "uppercase",
                                  letterSpacing: 1,
                                }}
                              >
                                {card.name}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            style={{
                              height: "16%",
                              width: "95%",
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              borderRadius: 8,
                              border: "3px solid #000",
                            }}
                          >
                            <Box
                              style={{
                                height: "78%",
                                width: "93%",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                borderRadius: 6,
                                borderTop: `3px solid ${Theme.palette.primary.light}`,
                                borderLeft: `3px solid ${Theme.palette.primary.light}`,
                                borderBottom: `3px solid ${Theme.palette.primary.dark}`,
                                borderRight: `3px solid ${Theme.palette.primary.dark}`,
                                backgroundColor: "#0070f7",
                              }}
                            >
                              {attributes.map((attribute, index) => {
                                return (
                                  <Box
                                    key={index}
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      marginBottom: 5,
                                      width: "24%",
                                      height: "100%",
                                      position: "relative",
                                    }}
                                  >
                                    <img
                                      src={attribute.img}
                                      style={{
                                        width: 24,
                                        height: 24,
                                        position: "absolute",
                                        objectFit: "contain",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        zIndex: 0,
                                      }}
                                    />
                                    <Typography
                                      style={{
                                        zIndex: 1,
                                        fontSize: 16,
                                        fontWeight: 800,
                                        color: "#fff",
                                        textShadow:
                                          "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                                      }}
                                    >
                                      {attribute.value}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
            </Grid>
          </Box>

          {selectedCard && (
            <ViewCardModal
              totalCards={
                showNFTCards
                  ? totalCharacterCardsOwned
                  : totalInfinityCardsOwned
              }
              card={selectedCard}
              usedToCraft={isUsedToCraftFinite(
                selectedCard.name,
                craftableCards
              )}
              craftTarget={craftableCards?.find(
                (c) => c.craftNeeded === selectedCard.name
              )}
              onClose={() => setSelectedCard(null)}
            />
          )}

          {/* STORE FOOTER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              backgroundColor: Theme.palette.primary.main,
              borderBottom: `5px solid ${Theme.palette.primary.dark}`,
              borderTop: `5px solid ${Theme.palette.primary.light}`,
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "60px",
              zIndex: 100,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "-60%",
                right: "2%",
                display: "flex",
                justifyContent: "space-evenly",
                p: 1,
                width: { xs: "95%", md: "70%" },
                height: "80px",
              }}
            >
              {/* Buttons here */}
              <Grid
                container
                md={12}
                sx={{
                  width: "100%",
                  display: "flex",
                  flexFlow: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  px: 2,
                  mb: 1,
                }}
              >
                <Grid
                  item
                  container
                  md={5}
                  lg={4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-evenly",
                    display: { xs: "none", md: "flex" },
                  }}
                >
                  <NeoButton
                    onClick={() => setShowNFTCards(false)}
                    text="Infinity Cards"
                    color={
                      !showNFTCards
                        ? Theme.palette.secondary.main
                        : Theme.palette.primary.main
                    }
                    width={40}
                  />
                  <NeoButton
                    onClick={() => setShowNFTCards(true)}
                    text="Character Cards"
                    color={
                      showNFTCards
                        ? Theme.palette.secondary.main
                        : Theme.palette.primary.main
                    }
                    width={40}
                  />
                </Grid>

                <Grid
                  item
                  container
                  xs={7}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-evenly",
                    display: { xs: "flex", md: "none" },
                  }}
                >
                  <NeoButton
                    onClick={() => setShowNFTCards(!showNFTCards)}
                    startAdorn={<LoopRounded />}
                    text={showNFTCards ? "Infinity Cards" : "Characters"}
                    color={
                      showNFTCards
                        ? Theme.palette.secondary.main
                        : Theme.palette.primary.main
                    }
                    width={98}
                  />
                </Grid>

                <Grid
                  item
                  container
                  xs={5}
                  md={5}
                  lg={4}
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    position: "relative",
                  }}
                >
                  {/* WEB VERSION */}
                  <Grid
                    item
                    container
                    xs={12}
                    sx={{
                      justifyContent: "flex-end",
                    }}
                  >
                    {/* filtering & sorting options */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* SORT SELECT */}

                      <Box id="sort-button" onClick={handleSortClick}>
                        <NeoButton
                          color={Theme.palette.tirciary.main}
                          isIcon={true}
                          icon={<SwapVertRounded />}
                          borderRadius={10}
                          onClick={() => {}}
                        />
                      </Box>
                      <Menu
                        id="sort-menu"
                        anchorEl={sortAnchorEl}
                        open={openSortMenu}
                        onClose={handleSortClose}
                        slotProps={{
                          list: {
                            "aria-labelledby": "sort-button",
                          },
                        }}
                      >
                        {showNFTCards ? (
                          <>
                            <MenuItem
                              value="name"
                              onClick={() => handleSelectOption("name")}
                            >
                              Name
                            </MenuItem>
                            <MenuItem
                              value="rarity"
                              onClick={() => handleSelectOption("rarity")}
                            >
                              Rarity
                            </MenuItem>
                            <MenuItem
                              value="element"
                              onClick={() => handleSelectOption("element")}
                            >
                              Element
                            </MenuItem>
                            <MenuItem
                              value="collection"
                              onClick={() => handleSelectOption("collection")}
                            >
                              Collection
                            </MenuItem>
                          </>
                        ) : (
                          <>
                            <MenuItem
                              value="name"
                              onClick={() => handleSelectOption("name")}
                            >
                              Name
                            </MenuItem>
                            <MenuItem
                              value="affectedArea"
                              onClick={() => handleSelectOption("affectedArea")}
                            >
                              Affected Area
                            </MenuItem>
                            <MenuItem
                              value="max"
                              onClick={() => handleSelectOption("max")}
                            >
                              Max Allowed
                            </MenuItem>
                            <MenuItem
                              value="amountOwned"
                              onClick={() => handleSelectOption("amountOwned")}
                            >
                              Amount Owned
                            </MenuItem>
                            <MenuItem
                              value="finite"
                              onClick={() => handleSelectOption("finite")}
                            >
                              Is Finite
                            </MenuItem>
                          </>
                        )}
                      </Menu>

                      {/* RARITY FILTER (for NFT cards) */}
                      {showNFTCards && (
                        <>
                          {isMobile ? (
                            <>
                              <Box
                                id="nft-filter-button"
                                aria-haspopup="true"
                                onClick={handleNftFilterClick}
                              >
                                <NeoButton
                                  color={Theme.palette.tirciary.main}
                                  isIcon={true}
                                  icon={<FilterListRounded />}
                                  borderRadius={10}
                                  onClick={() => {}}
                                />
                              </Box>

                              <Menu
                                id="nft-filter-menu"
                                anchorEl={nftFilterAnchorEl}
                                open={openNftFilter}
                                onClose={handleNftFilterClose}
                                slotProps={{
                                  list: {
                                    "aria-labelledby": "nft-filter-button",
                                  },
                                }}
                              >
                                <MenuItem
                                  value=""
                                  onClick={() => setOwnedOnly(!ownedOnly)}
                                >
                                  {ownedOnly ? "All Cards" : "My Cards Only"}
                                </MenuItem>
                                <MenuItem
                                  value=""
                                  onClick={() => handleFilterClick("")}
                                >
                                  All Rarities
                                </MenuItem>
                                <MenuItem
                                  value="Common"
                                  onClick={() => handleFilterClick("Common")}
                                >
                                  Common
                                </MenuItem>
                                <MenuItem
                                  value="Uncommon"
                                  onClick={() => handleFilterClick("Uncommon")}
                                >
                                  Uncommon
                                </MenuItem>
                                <MenuItem
                                  value="Rare"
                                  onClick={() => handleFilterClick("Rare")}
                                >
                                  Rare
                                </MenuItem>
                                <MenuItem
                                  value="Epic"
                                  onClick={() => handleFilterClick("Epic")}
                                >
                                  Epic
                                </MenuItem>
                                <MenuItem
                                  value="Legendary"
                                  onClick={() => handleFilterClick("Legendary")}
                                >
                                  Legendary
                                </MenuItem>
                              </Menu>
                            </>
                          ) : (
                            <Select
                              value={filterOptions.rarity[0] || ""}
                              onChange={(e) =>
                                setFilterOptions((prev) => ({
                                  ...prev,
                                  rarity: e.target.value
                                    ? [e.target.value]
                                    : [],
                                }))
                              }
                              displayEmpty
                              size="small"
                              sx={{ minWidth: 140, backgroundColor: "#fff" }}
                            >
                              <MenuItem
                                value=""
                                onClick={() => setOwnedOnly(!ownedOnly)}
                              >
                                {ownedOnly ? "All Cards" : "My Cards Only"}
                              </MenuItem>
                              <MenuItem value="">All Rarities</MenuItem>
                              <MenuItem value="Common">Common</MenuItem>
                              <MenuItem value="Uncommon">Uncommon</MenuItem>
                              <MenuItem value="Rare">Rare</MenuItem>
                              <MenuItem value="Epic">Epic</MenuItem>
                              <MenuItem value="Legendary">Legendary</MenuItem>
                            </Select>
                          )}
                        </>
                      )}

                      {/* FINITE FILTER (for Infinity cards) */}
                      {!showNFTCards && (
                        <>
                          {isMobile ? (
                            <>
                              <Box
                                id="char-filter-button"
                                onClick={handleCharFilterClick}
                              >
                                <NeoButton
                                  color={Theme.palette.tirciary.main}
                                  isIcon={true}
                                  icon={<FilterListRounded />}
                                  borderRadius={10}
                                  onClick={() => {}}
                                />
                              </Box>

                              <Menu
                                id="char-filter-menu"
                                anchorEl={charFilterAnchorEl}
                                open={openCharFilter}
                                onClose={handleCharFilterClose}
                                slotProps={{
                                  list: {
                                    "aria-labelledby": "char-filter-button",
                                  },
                                }}
                              >
                                <MenuItem
                                  value=""
                                  onClick={() => setOwnedOnly(!ownedOnly)}
                                >
                                  {ownedOnly ? "All Cards" : "My Cards Only"}
                                </MenuItem>
                                <MenuItem
                                  value=""
                                  onClick={() => handleFilterClick("")}
                                >
                                  All Types
                                </MenuItem>
                                <MenuItem
                                  value="TRUE"
                                  onClick={() => handleFilterClick("TRUE")}
                                >
                                  Finite Only
                                </MenuItem>
                                <MenuItem
                                  value="FALSE"
                                  onClick={() => handleFilterClick("FALSE")}
                                >
                                  Infinite Only
                                </MenuItem>
                              </Menu>
                            </>
                          ) : (
                            <Select
                              value={filterOptions.finite || ""}
                              onChange={(e) =>
                                setFilterOptions((prev) => ({
                                  ...prev,
                                  finite: e.target.value || null,
                                }))
                              }
                              displayEmpty
                              size="small"
                              sx={{ minWidth: 140, backgroundColor: "#fff" }}
                            >
                              <MenuItem
                                value=""
                                onClick={() => setOwnedOnly(!ownedOnly)}
                              >
                                {ownedOnly ? "All Cards" : "My Cards Only"}
                              </MenuItem>
                              <MenuItem value="">All Types</MenuItem>
                              <MenuItem value="TRUE">Finite Only</MenuItem>
                              <MenuItem value="FALSE">Infinite Only</MenuItem>
                            </Select>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>

                  {/* MOBILE VERSION */}
                  <Grid
                    imte
                    container
                    xs={12}
                    sx={{ display: { xs: "flex", md: "none" } }}
                  >
                    {/* filtering & sorting options */}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      )}

      {screen === "Decks" && (
        <MyDecksScreen
          reset={reset}
          decks={decks}
          setScreen={setScreen}
          newDeck={newDeck}
          setNewDeck={setNewDeck}
          deckName={deckName}
          selectedDeckID={selectedDeckID}
          setSelectedDeckID={setSelectedDeckID}
          setDeckName={setDeckName}
          setSelectedCharacter={setSelectedCharacter}
        />
      )}

      {screen === "Build" && (
        <DeckBuilder
          reset={reset}
          cards={cards}
          setScreen={setScreen}
          prevDeck={newDeck}
          setNewDeck={setNewDeck}
          name={deckName}
          deckID={selectedDeckID}
          setDeckName={setDeckName}
          setSelectedCharacter={setSelectedCharacter}
        />
      )}
    </>
  );
};

export default MyCardsScreen;
